import React, { Component } from 'react';
import { View, Platform, Switch, Animated, Dimensions } from 'react-native';

import { ViewPropTypes, TextPropTypes } from 'deprecated-react-native-prop-types';
import { isFunction as _isFunction } from 'lodash';
import PropTypes from 'prop-types';

import { VFText, Toggle, Icon } from '@vfgroup-oneplatform/foundation/Components';
import {
  Colors,
  withTheme,
  LightThemeColors,
} from '@vfgroup-oneplatform/foundation/Components/Themes';

import FrameworkTestKeys from '../../FrameworkTestKeys';
import { getThemeImages } from '../../Themes';

import styles from './PermissionsItem.Styles';

class PermissionsItem extends Component {
  static getDerivedStateFromProps(props, oldState) {
    if (props.initialValue !== oldState.toggleValue) {
      return { toggleValue: props.initialValue };
    }

    return null;
  }

  constructor(props) {
    super(props);

    this.initialize = false;
    this.animation = new Animated.Value(0);
    this.minHeight = 0;

    this.state = {
      toggleValue: props.initialValue,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.closed !== this.props.closed) {
      this.animateHeight();
    }
  }

  onToggleChange = value => {
    const { onAcceptanceChange } = this.props;
    const newValue = Platform.OS === 'android' ? value : !value;
    this.setState({ toggleValue: newValue });
    onAcceptanceChange(newValue);
  };

  _setHeight = event => {
    if (!this.initialize) {
      this.maxHeight = event.nativeEvent.layout.height;
      this.initialize = true;
      this.animation.setValue(this.maxHeight);
    }
  };

  animateHeight() {
    const initialValue = this.props.closed ? this.maxHeight + this.minHeight : this.minHeight;
    const finalValue = this.props.closed ? this.minHeight : this.maxHeight + this.minHeight;

    this.animation.setValue(initialValue);

    Animated.timing(this.animation, {
      toValue: finalValue,
      duration: 200,
      useNativeDriver: false,
    }).start();
  }

  renderIcon = hasIcon => {
    const { theme } = this.props;

    const images = this.props.images || getThemeImages(theme.name);

    return (
      hasIcon && (
        <Icon
          type="image"
          name={images[this.props.icon]}
          size={this.props.iconSize || 34}
          accessibilityLabel={FrameworkTestKeys.permissionsItemIcon}
          testID={FrameworkTestKeys.permissionsItemIcon}
        />
      )
    );
  };

  renderTitle = () => {
    const { titleStyle } = this.props;

    return (
      <VFText
        style={[styles.title, titleStyle]}
        i18nKey={this.props.title}
        testKey={FrameworkTestKeys.permissionsItemTitle}
      />
    );
  };

  renderRightComponent = isToggleAsText => {
    return isToggleAsText ? (
      <VFText
        style={styles.toggleAsText(this.props.theme)}
        i18nKey={
          !this.state.toggleValue
            ? 'app_permissions_status_blocked'
            : 'app_permissions_status_granted'
        }
        testKey={FrameworkTestKeys.permessionsItemToggleText}
      />
    ) : (
      this.renderToggle(Platform.OS === 'android')
    );
  };

  renderToggle = isAndroid => {
    const androidTrackColor = {
      false: 'rgb(182, 182, 182)',
      true: Colors.oceanRGBA(0.3),
    };
    const androidThumbColor = this.state.toggleValue ? Colors.ocean : 'rgb(236, 236, 236)';

    return isAndroid ? (
      <Switch
        value={this.state.toggleValue}
        onValueChange={this.onToggleChange}
        trackColor={androidTrackColor}
        thumbColor={androidThumbColor}
        testID={FrameworkTestKeys.permissionsItemSwitch}
      />
    ) : (
      <Toggle
        initialValue={this.props.initialValue}
        selected={this.state.toggleValue}
        onChange={this.onToggleChange}
        inactiveContainerStyle={styles.toggle}
        accessibilityLabel={FrameworkTestKeys.permissionsItemToggle}
        testID={FrameworkTestKeys.permissionsItemToggle}
      />
    );
  };

  renderDescription = hasDescription => {
    const { theme } = this.props;

    return (
      hasDescription && (
        <VFText
          style={[styles.description(theme), this.props.descriptionStyle]}
          i18nKey={this.props.description}
          testKey={FrameworkTestKeys.permissionsItemDescription}
        />
      )
    );
  };

  render() {
    const { isLastPermission, renderDescription, description, theme, isLast } = this.props;
    const opacity = this.animation.interpolate({
      inputRange: [0, 50, this.maxHeight || 150],
      outputRange: [0, 1, 1],
    });

    return (
      <Animated.View
        onLayout={this._setHeight}
        style={{
          height: this.initialize ? this.animation : undefined,
          opacity,
        }}
        testID={FrameworkTestKeys.permissionsItemDescriptionAnimatedView}
      >
        <View
          style={[styles.container(this.props.theme, isLastPermission), this.props.style]}
          testID={FrameworkTestKeys.permissionsItem}
        >
          <View style={styles.headerContainer} testID={FrameworkTestKeys.permissionsItemHeaderView}>
            {this.renderIcon(this.props.icon)}
            {this.renderTitle()}
            {this.renderRightComponent(this.props.withToggleAsText)}
          </View>

          {(_isFunction(renderDescription) && renderDescription()) ||
            this.renderDescription(description)}
          {Platform.OS === 'web' && Dimensions.get('window').width > 700 && !isLast && <View style={{ width: '100%', height: 1, backgroundColor: 'rgb(221, 221, 221)', marginHorizontal: 16, marginTop: 8 }} />}
        </View>
      </Animated.View>
    );
  }
}

PermissionsItem.defaultProps = {
  theme: LightThemeColors,
};

PermissionsItem.propTypes = {
  style: ViewPropTypes.style,
  titleStyle: TextPropTypes.style,
  icon: PropTypes.string,
  withToggleAsText: PropTypes.bool,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  descriptionStyle: PropTypes.object,
  onAcceptanceChange: PropTypes.func.isRequired,
  initialValue: PropTypes.bool,
  theme: PropTypes.object,
  closed: PropTypes.bool,
  iconSize: PropTypes.number,
  images: PropTypes.object,
  isLastPermission: PropTypes.bool,
  renderDescription: PropTypes.func,
};

export default withTheme(PermissionsItem);
